import { useRef, useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import type { FieldConfig } from "../types";

interface ExpandableTextAreaFieldProps<T> {
  field: FieldConfig<T>;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
}

export function ExpandableTextAreaField<T>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder,
  "aria-describedby": ariaDescribedBy,
  maxLength,
  showCharacterCount = false,
}: ExpandableTextAreaFieldProps<T>) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  
  // Auto-resize the textarea based on content
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate the new height
    const scrollHeight = textarea.scrollHeight;
    const minHeight = 40; // Match typical input height
    const maxHeight = 200; // Maximum expanded height
    
    // Set the height based on content, focus state, and limits
    if (!value && !isFocused) {
      textarea.style.height = `${minHeight}px`;
    } else {
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [value, isFocused]);
  
  // Adjust height when value changes
  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);
  
  // Character count calculation
  const characterCount = value?.length || 0;
  const isOverLimit = maxLength && characterCount > maxLength;
  
  return (
    <div className="relative space-y-1">
      <textarea
        ref={textareaRef}
        id={id}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-2",
          "text-sm ring-offset-background placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "resize-none transition-[height] duration-200 ease-in-out",
          "min-h-[40px] overflow-y-auto",
          isOverLimit && "border-destructive focus-visible:ring-destructive"
        )}
        placeholder={placeholder}
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-required={field.required || undefined}
        value={value || ""}
        onChange={(e) => {
          onChange(e.target.value);
          adjustHeight();
        }}
        onFocus={() => {
          setIsFocused(true);
          adjustHeight();
        }}
        onBlur={() => {
          setIsFocused(false);
          onBlur();
          adjustHeight();
        }}
        maxLength={maxLength}
        rows={1}
      />
      
      {showCharacterCount && (
        <div className={cn(
          "text-xs text-right",
          isOverLimit ? "text-destructive" : "text-muted-foreground"
        )}>
          {characterCount}
          {maxLength && ` / ${maxLength}`}
        </div>
      )}
    </div>
  );
}